import React, { useState, useRef, useCallback, useEffect } from 'react';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import LexicalErrorBoundary from '@lexical/react/LexicalErrorBoundary';
import {
    $getRoot,
    $createParagraphNode,
    $insertNodes,
    $getSelection,
    $isRangeSelection,
    $createLineBreakNode,
    FORMAT_TEXT_COMMAND,
    KEY_ENTER_COMMAND,
    COMMAND_PRIORITY_HIGH,
    COMMAND_PRIORITY_NORMAL,
    COMMAND_PRIORITY_LOW,
    PASTE_COMMAND,
    SELECTION_CHANGE_COMMAND,
} from 'lexical';
import { $generateHtmlFromNodes, $generateNodesFromDOM } from '@lexical/html';
import { $isLinkNode, TOGGLE_LINK_COMMAND, LinkNode, AutoLinkNode } from '@lexical/link';
import { $findMatchingParent } from '@lexical/utils';
import DOMPurify from 'dompurify';
import './RichTextInputComponent.scss';
import { ReactComponent as BoldIcon } from 'app/assets/icons/bold.svg';
import { ReactComponent as ItalicIcon } from 'app/assets/icons/italic.svg';
import { ReactComponent as UnderlineIcon } from 'app/assets/icons/underline.svg';
import { ReactComponent as LinkIcon } from 'app/assets/icons/link.svg';

// ─── Theme ───────────────────────────────────────────────────────────────────
const editorTheme = {
    root: 'lexical-root',
    paragraph: 'lexical-paragraph',
    // Theme classes control visual display inside the editor.
    // The exported HTML (saved to DB) independently uses <strong>/<em>/<u>
    // semantic tags via $generateHtmlFromNodes — no class names are saved.
    text: {
        bold: 'lexical-bold',
        italic: 'lexical-italic',
        underline: 'lexical-underline',
    },
    link: 'rich-text-link',
};

const ctrlPosMap = {
    'top': 'ctrls-top',
    'right': 'ctrls-right',
    'right-singleline': 'ctrls-right-single'
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function countLines(root) {
    let count = 0;
    root.getChildren().forEach(block => {
        count++;
        if (block.getChildren) {
            block.getChildren().forEach(child => {
                if (child.getType() === 'linebreak') count++;
            });
        }
    });
    return count;
}

function validateUrl(url) {
    if (!url || url.trim() === '') return 'Please enter a URL';
    if (!/^https?:\/\//i.test(url.trim())) return 'URL must start with http:// or https://';
    return '';
}

/**
 * Lexical's $generateHtmlFromNodes emits editor-display markup: every text node
 * is wrapped in <span style="white-space: pre-wrap">, formatted tags carry the
 * theme class names, and bold/italic are doubled (e.g. <b><strong>). This turns
 * that into clean semantic HTML for storage: <strong>, <em>, <u>, <a>.
 */
function cleanExportedHtml(rawHtml) {
    if (!rawHtml) return '';

    const doc = new DOMParser().parseFromString(`<div id="rte-root">${rawHtml}</div>`, 'text/html');
    const root = doc.getElementById('rte-root');

    // Strip editor-only class names and inline styles (e.g. white-space: pre-wrap).
    root.querySelectorAll('*').forEach((el) => {
        el.removeAttribute('class');
        el.removeAttribute('style');
    });

    // Unwrap <span> and <p> wrappers, keeping their contents inline.
    root.querySelectorAll('span, p').forEach((el) => {
        const parent = el.parentNode;
        while (el.firstChild) parent.insertBefore(el.firstChild, el);
        parent.removeChild(el);
    });

    // Collapse Lexical's doubled formatting tags into a single semantic tag.
    const collapse = (outerTag, innerTag) => {
        root.querySelectorAll(outerTag).forEach((el) => {
            if (
                el.childNodes.length === 1 &&
                el.firstChild.nodeType === 1 &&
                el.firstChild.tagName.toLowerCase() === innerTag
            ) {
                el.parentNode.replaceChild(el.firstChild, el);
            }
        });
    };
    collapse('b', 'strong');
    collapse('i', 'em');

    // Ensure links open safely.
    root.querySelectorAll('a[href]').forEach((a) => {
        a.setAttribute('target', '_blank');
        a.setAttribute('rel', 'noopener noreferrer');
    });

    let html = root.innerHTML;
    if (html === '<br>') html = '';
    return html;
}

// Allowed markup for the raw-HTML edit mode. Matches the saved/exported HTML
// shape (strong/em/u/a from the toolbar) plus a few extra tags/attrs editors
// may legitimately need to hand-author (e.g. branded <span class="...">).
const RAW_HTML_ALLOWED_TAGS = ['p', 'br', 'strong', 'em', 'u', 'b', 'i', 'span', 'a', 'ul', 'ol', 'li'];
const RAW_HTML_ALLOWED_ATTR = ['class', 'href', 'target', 'rel'];

function sanitizeRawHtml(html) {
    return DOMPurify.sanitize(html || '', {
        ALLOWED_TAGS: RAW_HTML_ALLOWED_TAGS,
        ALLOWED_ATTR: RAW_HTML_ALLOWED_ATTR,
    });
}

// ─── Plugins ─────────────────────────────────────────────────────────────────

/**
 * One-time initialisation: populates the editor from an HTML string prop.
 * Only runs on mount (empty dep array), ignores subsequent value changes
 * since the editor is uncontrolled after init (caller gets updates via onValueChange).
 */
function InitFromHtmlPlugin({ html }) {
    const [editor] = useLexicalComposerContext();
    const initialized = useRef(false);

    useEffect(() => {
        if (initialized.current || !html) return;
        initialized.current = true;

        editor.update(() => {
            const parser = new DOMParser();
            const dom = parser.parseFromString(html, 'text/html');
            const nodes = $generateNodesFromDOM(editor, dom);
            const root = $getRoot();
            root.clear();
            if (nodes.length) {
                const para = $createParagraphNode();
                root.append(para);
                para.select();
                $insertNodes(nodes);
            }
        });
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    return null;
}

/**
 * Enforces maxLines: Enter always inserts a soft line-break (never a new paragraph block),
 * and blocks Enter/paste when the limit would be exceeded.
 */
function ConstraintsPlugin({ maxLines, disabled }) {
    const [editor] = useLexicalComposerContext();

    useEffect(() => {
        const removeEnter = editor.registerCommand(
            KEY_ENTER_COMMAND,
            (event) => {
                if (disabled) return true;
                event && event.preventDefault();

                editor.update(() => {
                    const root = $getRoot();
                    const limit = maxLines || 999;
                    if (countLines(root) >= limit) return; // at limit – block

                    const sel = $getSelection();
                    if ($isRangeSelection(sel)) {
                        sel.insertNodes([$createLineBreakNode()]);
                    }
                });

                return true; // handled – prevents default paragraph split
            },
            COMMAND_PRIORITY_HIGH
        );

        const removePaste = editor.registerCommand(
            PASTE_COMMAND,
            (event) => {
                if (disabled) return false;
                const text = (event && event.clipboardData && event.clipboardData.getData('text/plain')) || '';
                const incoming = text.split('\n').length;
                const current = countLines($getRoot());
                if (current + incoming - 1 > (maxLines || 999)) {
                    event && event.preventDefault();
                    return true; // block paste
                }
                return false; // allow
            },
            COMMAND_PRIORITY_NORMAL
        );

        return () => {
            removeEnter();
            removePaste();
        };
    }, [editor, maxLines, disabled]);

    return null;
}

/**
 * Reads active formats and link state from the selection on every selection change,
 * calling onStateChange({ activeFormats: Set, currentUrl: string|null }).
 */
function ToolbarStatePlugin({ onStateChange }) {
    const [editor] = useLexicalComposerContext();

    useEffect(() => {
        return editor.registerCommand(
            SELECTION_CHANGE_COMMAND,
            () => {
                editor.getEditorState().read(() => {
                    const sel = $getSelection();
                    if (!$isRangeSelection(sel)) {
                        onStateChange({ activeFormats: new Set(), currentUrl: null });
                        return;
                    }

                    const activeFormats = new Set();
                    if (sel.hasFormat('bold')) activeFormats.add('BOLD');
                    if (sel.hasFormat('italic')) activeFormats.add('ITALIC');
                    if (sel.hasFormat('underline')) activeFormats.add('UNDERLINE');

                    const anchorNode = sel.anchor.getNode();
                    const linkNode = $findMatchingParent(anchorNode, $isLinkNode);
                    const currentUrl = linkNode ? linkNode.getURL() : null;

                    onStateChange({ activeFormats, currentUrl });
                });
                return false;
            },
            COMMAND_PRIORITY_LOW
        );
    }, [editor, onStateChange]);

    return null;
}

/**
 * Exposes the Lexical editor instance to the parent via a ref so the toolbar
 * can dispatch commands (format, toggle link) without going through React state.
 */
function EditorRefPlugin({ editorRef }) {
    const [editor] = useLexicalComposerContext();
    useEffect(() => {
        editorRef.current = editor;
    }, [editor, editorRef]);
    return null;
}

// ─── Main component ───────────────────────────────────────────────────────────

function RichTextInput({
    value,
    onValueChange,
    id,
    disabled,
    hideCtrls,
    ctrlsPosition,
    maxLines,
    editorLines,
    exactHeight,
    textAlign,
    label,
    error,
    className,
    allowRawHtmlEdit,
    isHtmlEdited,
}) {
    const editorRef = useRef(null);
    const linkInputRef = useRef(null);
    const onChangeInitGuard = useRef(!value);

    const [activeFormats, setActiveFormats] = useState(new Set());
    const [currentLinkUrl, setCurrentLinkUrl] = useState(null);
    const [showLinkPopup, setShowLinkPopup] = useState(false);
    const [linkUrl, setLinkUrl] = useState('');
    const [linkEditMode, setLinkEditMode] = useState(false);
    const [linkUrlError, setLinkUrlError] = useState('');
    const [linkTooltipVisible, setLinkTooltipVisible] = useState(false);
    const [isRawMode, setIsRawMode] = useState(() => !!(allowRawHtmlEdit && isHtmlEdited));
    const [rawHtmlDraft, setRawHtmlDraft] = useState(() => (allowRawHtmlEdit && isHtmlEdited) ? sanitizeRawHtml(value) : '');
    const [showRawModeWarning, setShowRawModeWarning] = useState(false);
    const [htmlEditedFlag, setHtmlEditedFlag] = useState(!!isHtmlEdited);

    const isOnLink = currentLinkUrl !== null;
    const showLinkTooltip = isOnLink && linkTooltipVisible && !showLinkPopup;

    // ── Raw HTML mode helpers ─────────────────────────────────────────────────
    // Toggling back to the Lexical editor remounts <LexicalComposer>, which
    // fires handleChange once on its own initial render (same as first mount).
    // Reset the guard here (synchronously, not via effect) so that spurious
    // fire doesn't immediately re-save and strip markup Lexical doesn't model
    // (e.g. a hand-authored <span class="...">, since only LinkNode/AutoLinkNode
    // are registered node types).
    const exitRawMode = useCallback((nextValue) => {
        onChangeInitGuard.current = !nextValue;
        setIsRawMode(false);
    }, []);

    const enterRawMode = useCallback(() => {
        setRawHtmlDraft(sanitizeRawHtml(value));
        setIsRawMode(true);
    }, [value]);

    // Unchecking "Edit as HTML" doesn't switch modes immediately — it asks for
    // confirmation first, since going back to the Text Editor re-renders from
    // Lexical's node model and can't faithfully represent markup typed in HTML
    // mode (e.g. a <span class="...">, since only LinkNode/AutoLinkNode are
    // registered Lexical node types).
    const requestExitRawMode = useCallback(() => {
        if (htmlEditedFlag) {
            setShowRawModeWarning(true);
        } else {
            exitRawMode(value);
        }
    }, [htmlEditedFlag, value, exitRawMode]);

    const confirmExitRawMode = useCallback(() => {
        setShowRawModeWarning(false);
        exitRawMode(value);
    }, [value, exitRawMode]);

    const cancelExitRawMode = useCallback(() => {
        setShowRawModeWarning(false);
    }, []);

    // Raw mode has no explicit Save/Cancel: every keystroke live-commits the
    // sanitized HTML, same as the visual editor and the other plain-text
    // inputs on this page (description textarea, etc).
    const handleRawHtmlChange = useCallback((e) => {
        const newHtml = e.target.value;
        setRawHtmlDraft(newHtml);
        setHtmlEditedFlag(true);
        onValueChange && onValueChange(sanitizeRawHtml(newHtml), id, true);
    }, [onValueChange, id]);

    // ── Toolbar state callback ────────────────────────────────────────────────
    const onToolbarStateChange = useCallback(({ activeFormats: af, currentUrl }) => {
        setActiveFormats(af);
        setCurrentLinkUrl(currentUrl);
    }, []);

    // ── Link popup helpers ────────────────────────────────────────────────────
    const closeLinkPopup = useCallback(() => {
        setShowLinkPopup(false);
        setLinkUrl('');
        setLinkEditMode(false);
        setLinkUrlError('');
    }, []);

    const openLinkPopup = useCallback((url, editMode, focusInput) => {
        setShowLinkPopup(true);
        setLinkUrl(url || '');
        setLinkEditMode(editMode || false);
        setLinkUrlError('');
        if (focusInput) {
            setTimeout(() => linkInputRef.current && linkInputRef.current.focus(), 0);
        }
    }, []);

    const toggleLinkPopup = useCallback((e) => {
        e && e.preventDefault();
        if (showLinkPopup) {
            closeLinkPopup();
            return;
        }
        if (isOnLink) {
            openLinkPopup(currentLinkUrl, true, true);
            return;
        }
        // Only open for new link when text is selected (non-collapsed)
        // We rely on the Lexical editor having a selection; if collapsed with no link, do nothing
        openLinkPopup('', false, true);
    }, [showLinkPopup, isOnLink, currentLinkUrl, closeLinkPopup, openLinkPopup]);

    const confirmLink = useCallback(() => {
        const url = linkUrl.trim();
        if (!url) {
            closeLinkPopup();
            return;
        }
        const err = validateUrl(url);
        if (err) {
            setLinkUrlError(err);
            return;
        }
        if (editorRef.current) {
            editorRef.current.dispatchCommand(TOGGLE_LINK_COMMAND, {
                url,
                target: '_blank',
                rel: 'noopener noreferrer',
            });
        }
        closeLinkPopup();
    }, [linkUrl, closeLinkPopup]);

    const removeLinkHandler = useCallback((e) => {
        e && e.preventDefault();
        if (editorRef.current) {
            editorRef.current.dispatchCommand(TOGGLE_LINK_COMMAND, null);
        }
        closeLinkPopup();
        setLinkTooltipVisible(false);
    }, [closeLinkPopup]);

    const handleEditorClick = useCallback(() => {
        // Read the link directly from the editor instead of relying on
        // currentLinkUrl state, which is updated asynchronously by
        // SELECTION_CHANGE_COMMAND and may still be stale on the first click.
        setTimeout(() => {
            const editor = editorRef.current;
            if (!editor) return;
            editor.getEditorState().read(() => {
                const sel = $getSelection();
                let url = null;
                if ($isRangeSelection(sel)) {
                    const node = $findMatchingParent(sel.anchor.getNode(), $isLinkNode);
                    url = node ? node.getURL() : null;
                }
                setCurrentLinkUrl(url);
                setLinkTooltipVisible(url !== null);
            });
        }, 0);
    }, []);

    const handleLinkUrlChange = useCallback((e) => {
        setLinkUrl(e.target.value);
        setLinkUrlError('');
    }, []);

    const handleLinkKeyDown = useCallback((e) => {
        if (e.key === 'Enter') { e.preventDefault(); confirmLink(); }
        if (e.key === 'Escape') { e.preventDefault(); closeLinkPopup(); }
    }, [confirmLink, closeLinkPopup]);

    // ── HTML output callback ──────────────────────────────────────────────────
    const handleChange = useCallback((editorState) => {
        // Skip the very first fire (Lexical fires once on mount before InitFromHtmlPlugin runs)
        if (!onChangeInitGuard.current) {
            onChangeInitGuard.current = true;
            return;
        }
        editorState.read(() => {
            const rawHtml = $generateHtmlFromNodes(editorRef.current);
            const html = cleanExportedHtml(rawHtml);
            // Don't clear htmlEditedFlag here: the "Contains HTML" badge should
            // stay visible through Text Editor edits (the pending is_html_edited=false
            // is sent below and only takes visible effect once the save completes
            // and the row remounts with the refreshed isHtmlEdited prop).
            onValueChange && onValueChange(html, id, false);
        });
    }, [onValueChange, id]);

    // ── Layout ────────────────────────────────────────────────────────────────
    const cls1 = hideCtrls ? 'hide-controls' : (ctrlPosMap[ctrlsPosition || 'top']);
    const cls2 = label ? 'hasLabel' : '';

    const style = {
        height: exactHeight
            ? exactHeight
            : Math.min(maxLines, editorLines || maxLines) * 24 + 16,
        textAlign: textAlign || 'left',
    };

    // ── Lexical config ────────────────────────────────────────────────────────
    const initialConfig = {
        namespace: `RichTextInput_${id || 'default'}`,
        theme: editorTheme,
        nodes: [LinkNode, AutoLinkNode],
        editorState: null,
        onError: console.error,
    };

    // ── Toolbar ───────────────────────────────────────────────────────────────
    const formatButtons = [
        { style: 'BOLD', Icon: BoldIcon },
        { style: 'ITALIC', Icon: ItalicIcon },
        { style: 'UNDERLINE', Icon: UnderlineIcon },
    ];

    const ctrls = !hideCtrls && (
        <div className="format-controls">
            {allowRawHtmlEdit && (
                <div className="raw-html-toggle-wrapper">
                    {!isRawMode && htmlEditedFlag && (
                        <span
                            className="raw-html-indicator"
                            title="This text was edited in HTML mode and may contain custom markup the Text Editor can't fully display."
                        >
                            Contains HTML
                        </span>
                    )}
                    <label className="raw-html-toggle">
                        <input
                            type="checkbox"
                            checked={isRawMode}
                            disabled={disabled}
                            onChange={(e) => {
                                if (e.target.checked) {
                                    enterRawMode();
                                } else {
                                    requestExitRawMode();
                                }
                            }}
                        />
                        Edit as HTML
                    </label>
                </div>
            )}
            {!isRawMode && (
            <div className="format-buttons">
                {formatButtons.map(({ style: fmt, Icon }) => (
                    <button
                        key={fmt}
                        type="button"
                        disabled={disabled}
                        data-style={fmt}
                        className={`${fmt}${activeFormats.has(fmt) ? ' active' : ''}`}
                        onClick={(e) => {
                            e.preventDefault();
                            editorRef.current && editorRef.current.dispatchCommand(FORMAT_TEXT_COMMAND, fmt.toLowerCase());
                        }}
                        onMouseDown={(e) => e.preventDefault()}
                    >
                        <Icon />
                    </button>
                ))}
                <button
                    type="button"
                    disabled={disabled}
                    data-style="LINK"
                    className={`LINK${isOnLink || showLinkPopup ? ' active' : ''}`}
                    onClick={toggleLinkPopup}
                    onMouseDown={(e) => e.preventDefault()}
                >
                    <LinkIcon />
                </button>
            </div>
            )}

            {!isRawMode && showLinkTooltip && (
                <div className="link-tooltip">
                    <span className="link-tooltip-url" title={currentLinkUrl}>{currentLinkUrl}</span>
                    <div className="link-tooltip-buttons">
                        <button
                            type="button"
                            className="link-tooltip-edit"
                            onClick={(e) => { e.preventDefault(); e.stopPropagation(); openLinkPopup(currentLinkUrl, true, true); }}
                            onMouseDown={(e) => e.preventDefault()}
                            title="Edit link"
                        >
                            Edit
                        </button>
                        <button
                            type="button"
                            className="link-tooltip-remove"
                            onClick={removeLinkHandler}
                            onMouseDown={(e) => e.preventDefault()}
                            title="Remove link"
                        >
                            Remove
                        </button>
                        <button
                            type="button"
                            className="link-tooltip-close"
                            onClick={(e) => { e.preventDefault(); e.stopPropagation(); setLinkTooltipVisible(false); }}
                            onMouseDown={(e) => e.preventDefault()}
                            title="Close"
                        >
                            ✕
                        </button>
                    </div>
                </div>
            )}

            {!isRawMode && showLinkPopup && (
                <div className="link-popup">
                    <div className="link-popup-content">
                        <input
                            ref={linkInputRef}
                            type="text"
                            placeholder="https://example.com"
                            value={linkUrl}
                            onChange={handleLinkUrlChange}
                            onKeyDown={handleLinkKeyDown}
                            className={linkUrlError ? 'has-error' : ''}
                        />
                        {linkUrlError && (
                            <span className="link-url-error">{linkUrlError}</span>
                        )}
                        <div className="link-popup-buttons">
                            <button
                                type="button"
                                className="link-confirm"
                                onClick={(e) => { e.preventDefault(); e.stopPropagation(); confirmLink(); }}
                                onMouseDown={(e) => e.preventDefault()}
                            >
                                {linkEditMode ? 'Update' : 'Add'}
                            </button>
                            {linkEditMode && (
                                <button
                                    type="button"
                                    className="link-remove"
                                    onClick={removeLinkHandler}
                                    onMouseDown={(e) => e.preventDefault()}
                                >
                                    Remove
                                </button>
                            )}
                            <button
                                type="button"
                                className="link-cancel"
                                onClick={(e) => { e.preventDefault(); e.stopPropagation(); closeLinkPopup(); }}
                                onMouseDown={(e) => e.preventDefault()}
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );

    // ── Render ────────────────────────────────────────────────────────────────
    return (
        <div className={`RichTextInputComponent ${className || ''} ${error ? 'error' : ''} ${cls1} ${cls2}`}>
            {label}
            <div className="RTEcontainer">
                {ctrls}
                {isRawMode ? (
                    <div className={`editor-field raw-html-field ${disabled ? 'disabled' : ''}`} style={style}>
                        <textarea
                            className="raw-html-textarea"
                            value={rawHtmlDraft}
                            disabled={disabled}
                            spellCheck={false}
                            onChange={handleRawHtmlChange}
                        />
                    </div>
                ) : (
                    <LexicalComposer initialConfig={initialConfig}>
                        <div
                            className={`editor-field ${disabled ? 'disabled' : ''}`}
                            style={style}
                            onClick={handleEditorClick}
                        >
                            <RichTextPlugin
                                contentEditable={
                                    <ContentEditable
                                        className="lexical-content-editable"
                                        aria-readonly={disabled || undefined}
                                        spellCheck={false}
                                    />
                                }
                                placeholder={null}
                                ErrorBoundary={LexicalErrorBoundary}
                            />
                            <HistoryPlugin />
                            <LinkPlugin />
                            <InitFromHtmlPlugin html={value} />
                            <ConstraintsPlugin maxLines={maxLines} disabled={disabled} />
                            <ToolbarStatePlugin onStateChange={onToolbarStateChange} />
                            <EditorRefPlugin editorRef={editorRef} />
                            <OnChangePlugin onChange={handleChange} />
                        </div>
                    </LexicalComposer>
                )}
                {error && <span className="RTE_error">{error}</span>}
            </div>
            {showRawModeWarning && (
                <div className="raw-html-warning-overlay">
                    <div className="raw-html-warning-popup">
                        <p>Warning: Editing in the Text Editor mode will overwrite changes made in HTML mode</p>
                        <div className="raw-html-warning-buttons">
                            <button
                                type="button"
                                className="raw-html-warning-continue"
                                onClick={(e) => { e.preventDefault(); confirmExitRawMode(); }}
                                onMouseDown={(e) => e.preventDefault()}
                            >
                                Continue
                            </button>
                            <button
                                type="button"
                                className="raw-html-warning-cancel"
                                onClick={(e) => { e.preventDefault(); cancelExitRawMode(); }}
                                onMouseDown={(e) => e.preventDefault()}
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default RichTextInput;
export { RichTextInput };